import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, CheckCircle2, Circle, ShieldAlert, ClipboardCheck } from 'lucide-react';
import {
  getBookingDocumentRequirements,
  recordBookingDocumentOverride,
  type BookingDocumentRequirement,
} from '../../lib/documents';
import DocumentFillModal from './DocumentFillModal';

interface BookingDocumentsGateModalProps {
  businessId: string;
  bookingId: string;
  /** Show the admin override controls (server still enforces admin). */
  isAdmin: boolean;
  onClose: () => void;
  /** Called when requirements are satisfied (all required done, or admin overrode) — caller completes the booking. */
  onProceed: () => void;
}

export default function BookingDocumentsGateModal({ businessId, bookingId, isAdmin, onClose, onProceed }: BookingDocumentsGateModalProps) {
  const [loading, setLoading] = useState(true);
  const [reqs, setReqs] = useState<BookingDocumentRequirement[]>([]);
  const [fillTemplateId, setFillTemplateId] = useState<string | null>(null);
  const [showOverride, setShowOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const data = await getBookingDocumentRequirements(businessId, bookingId);
      setReqs(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [businessId, bookingId]);

  const incompleteRequired = reqs.filter((r) => r.enforcement === 'required' && !r.completed);
  const allRequiredDone = incompleteRequired.length === 0;

  const handleOverride = async () => {
    setError(null);
    if (!reason.trim()) { setError('Please enter a reason for the override.'); return; }
    setWorking(true);
    try {
      // Log one override row per still-incomplete required doc.
      for (const r of incompleteRequired) {
        await recordBookingDocumentOverride({ businessId, bookingId, templateId: r.template_id, reason: reason.trim() });
      }
      onProceed();
    } catch (e: any) {
      setError(e?.message || 'Failed to override.');
    } finally {
      setWorking(false);
    }
  };

  const content = (
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-center justify-center p-4" onClick={working ? undefined : onClose}>
      <div className="bg-gray-100 rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white rounded-t-2xl shrink-0">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-emerald-600" />
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">Before completing</div>
              <div className="text-[15px] font-semibold text-gray-900">Required documents</div>
            </div>
          </div>
          <button onClick={onClose} disabled={working} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
          {loading ? (
            <div className="flex justify-center py-12 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              <p className="text-[13px] text-gray-600">
                {allRequiredDone ? 'All required documents are complete.' : 'These documents must be completed before this booking can be marked complete.'}
              </p>
              <div className="space-y-2">
                {reqs.map((r) => (
                  <div key={r.procedure_id} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3">
                    {r.completed
                      ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                      : <Circle className="h-5 w-5 text-gray-300 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-gray-900 truncate">{r.title || 'Document'}</div>
                      <div className="text-[11px] text-gray-500">
                        {r.facing === 'customer' ? 'Customer sign-off' : 'Staff checklist'} · {r.enforcement === 'required' ? 'Required' : 'Optional'}
                      </div>
                    </div>
                    {!r.completed && (
                      <button onClick={() => setFillTemplateId(r.template_id)} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700">
                        {r.facing === 'customer' ? 'Sign' : 'Fill'}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {!allRequiredDone && isAdmin && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  {!showOverride ? (
                    <button onClick={() => setShowOverride(true)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-amber-700 hover:text-amber-800">
                      <ShieldAlert className="h-4 w-4" /> Override &amp; complete anyway
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-[12px] font-semibold text-amber-800 flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" /> Admin override</div>
                      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason for completing without the required document(s)…" className="w-full text-[13px] border border-amber-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400/40" />
                      <p className="text-[11px] text-amber-700">This is logged to the booking's record.</p>
                    </div>
                  )}
                </div>
              )}

              {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-white rounded-b-2xl flex justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={working} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          {allRequiredDone ? (
            <button onClick={onProceed} disabled={working} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-[14px] font-semibold hover:bg-emerald-700 disabled:opacity-60">
              <CheckCircle2 className="h-4 w-4" /> Complete booking
            </button>
          ) : showOverride ? (
            <button onClick={handleOverride} disabled={working || !reason.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 text-white text-[14px] font-semibold hover:bg-amber-700 disabled:opacity-60">
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />} Override &amp; complete
            </button>
          ) : null}
        </div>
      </div>

      {fillTemplateId && (
        <DocumentFillModal
          templateId={fillTemplateId}
          contextType="booking"
          contextId={bookingId}
          onClose={() => setFillTemplateId(null)}
          onCompleted={() => { setFillTemplateId(null); void load(); }}
        />
      )}
    </div>
  );

  return createPortal(content, document.body);
}
